// Промежуточные дела (редизайн): бэк полностью владеет состоянием таймера
// каждого дела — несколько параллельных дел у одного пользователя, живой
// мониторинг по WS /ws/intermediate. Это ОТДЕЛЬНАЯ система от общего таймера
// обычных задач (/tasks/:id/timer/start|pause) — тот не менялся.
import { http } from '@/shared/api/http';
import type { DeepRequired, Schema } from '@/shared/api';

export type IntermediateStatus =
  Schema<'tasks_module_internal_models.IntermediateTaskStatus'>;

/** Полное состояние трекинга одного дела — форма всех ответов и WS-снапшотов. */
export type IntermediateState = Omit<
  DeepRequired<Schema<'tasks_module_internal_models.IntermediateTaskState'>>,
  'status' | 'description' | 'segmentStartedAt' | 'completedAt'
> & {
  status: IntermediateStatus;
  description?: string | null;
  segmentStartedAt?: string | null;
  completedAt?: string;
};

/** Секундный тик по одному running-делу (intermediate_timer_tick — массив таких). */
export interface IntermediateTick {
  taskId: number;
  elapsedSeconds: number;
  remainingSeconds: number;
}

/** POST /tasks/intermediate — создать и сразу запустить дело (без привязки к
 *  проекту/группе). durationMinutes по умолчанию 20. Ответ — полное состояние. */
export async function createIntermediate(payload: {
  description?: string;
  durationMinutes?: number;
}): Promise<IntermediateState> {
  const { data } = await http.post<IntermediateState>(
    '/tasks/intermediate',
    payload
  );
  return data;
}

/** GET /tasks/intermediate — все НЕ завершённые (running/paused) дела текущего
 *  пользователя с уже посчитанными elapsed/remaining. */
export async function listIntermediate(): Promise<IntermediateState[]> {
  const { data } = await http.get<IntermediateState[]>('/tasks/intermediate');
  return Array.isArray(data) ? data : [];
}

/** Идемпотентна; сразу пытается записать накопленное время в Bitrix
 *  (при сбое — 502, секунды копятся в pendingSeconds). */
export async function pauseIntermediate(
  taskId: number
): Promise<IntermediateState> {
  const { data } = await http.post<IntermediateState>(
    `/tasks/intermediate/${taskId}/pause`
  );
  return data;
}

/** Идемпотентна. */
export async function resumeIntermediate(
  taskId: number
): Promise<IntermediateState> {
  const { data } = await http.post<IntermediateState>(
    `/tasks/intermediate/${taskId}/resume`
  );
  return data;
}

/** PATCH /time — сдвигает ПЛАН и дедлайн на дельту (не факт). При отказе
 *  Bitrix — 502, локальное состояние на бэке не меняется. */
export async function adjustIntermediatePlan(
  taskId: number,
  deltaMinutes: number
): Promise<IntermediateState> {
  const { data } = await http.patch<IntermediateState>(
    `/tasks/intermediate/${taskId}/time`,
    { deltaMinutes }
  );
  return data;
}

/** Завершение: флаш последнего отрезка → закрытие в Bitrix с перечиткой
 *  статуса. При сбое флаша завершение отменяется целиком (дело остаётся
 *  paused, время не теряется) — придёт 502. */
export async function completeIntermediate(
  taskId: number
): Promise<IntermediateState> {
  const { data } = await http.post<IntermediateState>(
    `/tasks/intermediate/${taskId}/complete`
  );
  return data;
}

/** Человекочитаемое сообщение для ошибок действий над делом (коды из спеки). */
export function intermediateErrorMessage(error: ANY): string {
  const status = error?.response?.status;
  const backendText = String(error?.response?.data?.error ?? '').trim();
  switch (status) {
    case 502:
      return 'Bitrix отклонил запись — время не потеряно, попробуйте ещё раз позже';
    case 409:
      return backendText || 'Дело уже завершено';
    case 404:
      return 'Дело не найдено — обновите список';
    case 403:
      return 'Дело принадлежит другому пользователю';
    default:
      return backendText || 'Не удалось выполнить действие с делом';
  }
}
